import React from 'react';
import { format, parseISO } from 'date-fns';
import { Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Vertical grid of time slot buttons for the selected date.
 *   slots = [{ time: "...", status: "available" | "booked" }, ...]
 *
 * Booked slots stay visible (greyed + struck through with a "Booked"
 * label) so the page reads as in-demand. Only "available" slots
 * accept clicks.
 *
 * `compact` shrinks the grid to 2 columns so it fits next to the date
 * picker in side-by-side layout.
 */
export function TimeSlotGrid({ slots, selectedSlot, onSelect, compact = false }) {
  if (!slots || slots.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed py-12 text-center text-sm text-muted-foreground">
        <Clock className="size-5" />
        <p>No times available on this date.</p>
        <p className="text-xs">Try a different date.</p>
      </div>
    );
  }

  const availableCount = slots.filter(s => s.status === 'available').length;

  return (
    <div className="space-y-3 animate-fade-in">
      {availableCount > 0 && availableCount <= 6 && (
        <p className="text-xs text-muted-foreground">
          Only <span className="font-semibold text-foreground">{availableCount}</span>{' '}
          slot{availableCount === 1 ? '' : 's'} left on this date.
        </p>
      )}
      <div
        className={cn(
          'grid gap-2 overflow-y-auto pr-1',
          compact
            ? 'max-h-[460px] grid-cols-2'
            : 'max-h-[420px] grid-cols-2 sm:grid-cols-3'
        )}
      >
        {slots.map((slot) => {
          const t = parseISO(slot.time);
          const label = format(t, 'h:mm a');
          const isSelected = selectedSlot?.time === slot.time;
          const isBooked = slot.status === 'booked';
          return (
            <button
              key={slot.time}
              type="button"
              onClick={() => !isBooked && onSelect(slot)}
              disabled={isBooked}
              aria-disabled={isBooked}
              className={cn(
                'flex h-10 items-center justify-center rounded-md border text-sm font-medium transition-all tabular',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                isBooked &&
                  'cursor-not-allowed border-dashed border-border bg-muted/40 text-muted-foreground line-through opacity-70',
                !isBooked && isSelected &&
                  'border-primary bg-primary text-primary-foreground shadow-soft',
                !isBooked && !isSelected &&
                  'border-input bg-background text-foreground hover:border-primary hover:bg-primary-soft hover:text-primary'
              )}
              title={isBooked ? 'Already booked' : undefined}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
